import services from './services';
import { subscribe } from '../../../utils/socket';
import events from '@/utils/helpers/events';
import i18n, { routeLocale } from '@/i18n';
import { SWAP_FILTER_TYPES } from '../swapList';

let lastInterest = null;

async function init({ dispatch, rootState }) {
  subscribe('interest', async data => {
    const { interested, propositionId, propositionId2 } = data;

    const currentUserProposition =
      rootState.myPropositions.primaryProposition.propositionId;
    if (currentUserProposition != propositionId) {
      return;
    }

    // Prevent action on echo
    if (
      lastInterest &&
      lastInterest.propositionId == propositionId &&
      propositionId2 == lastInterest.targetPropositionId &&
      interested === lastInterest.interested
    ) {
      return;
    }

    dispatch('markInterest', {
      interested,
      targetPropositionId: propositionId2,
      skipRequest: true
    });

    dispatch('swapList/getSwapInterestCounts', null, { root: true });
  });
}

// superLike: send interest 2 (a "yes" everywhere downstream, plus the local
// superLikedIds set and the balance). previousInterested: the mark to restore
// if the request is refused — a superlike upgrade of an existing yes must fall
// back to that yes, not to unmarked.
async function markInterest(
  context,
  {
    targetPropositionId,
    interested,
    skipRequest,
    where,
    confirmedEndMatch,
    superLike = false,
    previousInterested,
    // Which swap-list tab the mark was made from (top-picks / incoming /
    // interested / removed); undefined from surfaces without tabs.
    triggeredFromSection
  }
) {
  window.Logger.log({ targetPropositionId, interested, superLike });
  const currentUserProposition =
    context.rootState.myPropositions.primaryProposition.propositionId; // primary proposition

  // Confirm before un-marking interest if there's an active match
  if (!skipRequest && !confirmedEndMatch && interested !== true) {
    let hasActive = false;
    try {
      hasActive = await services.hasActiveMatches({
        propositionId: currentUserProposition.toString(),
        propositionId2: targetPropositionId.toString()
      });
    } catch (e) {
      window.Logger.log('hasActiveMatches check failed', e);
    }
    if (hasActive) {
      let address = '';
      try {
        address = await services.fetchPropositionAddress(targetPropositionId);
      } catch (e) {
        window.Logger.log('fetchPropositionAddress failed', e);
      }
      return new Promise((resolve, reject) => {
        context.dispatch(
          'ui/showEndMatchConfirm',
          {
            params: { targetPropositionId, interested, where, address },
            onConfirm: async () => {
              try {
                const result = await context.dispatch('markInterest', {
                  targetPropositionId,
                  interested,
                  where,
                  triggeredFromSection,
                  confirmedEndMatch: true
                });
                resolve(result);
              } catch (err) {
                reject(err);
              }
            },
            onCancel: () => {
              reject(new Error('END_MATCH_CANCELLED'));
            }
          },
          { root: true }
        );
      });
    }
  }

  let interest;
  if (superLike && interested === true) {
    interest = 2;
  } else if (interested === true) {
    interest = 1;
  } else if (interested === false) {
    interest = 0;
  } else {
    interest = null;
  }
  const isSuperLike = interest === 2;
  // A ✕/unmark clears the star optimistically; restored if the request fails.
  const wasSuperLiked = (context.state.superLikedIds || []).includes(
    String(targetPropositionId)
  );

  // dispatch events to update vuex states where needed
  context.dispatch('setInterestStatus', {
    targetPropositionId,
    interested,
    ...(isSuperLike ? { superLike: true } : {})
  });

  if (skipRequest) return;

  // for checking socket echoing
  lastInterest = {
    interested,
    propositionId: currentUserProposition,
    targetPropositionId
  };

  events.emitEvent(events.eventTypes.CLICK, 'Interest', {
    src: where,
    interested,
    ...(isSuperLike ? { superLike: true } : {})
  });

  // send request
  let likeFailed = false;

  try {
    const response = await services.markInterest({
      propositionId: currentUserProposition.toString(),
      propositionId2: targetPropositionId.toString(),
      interest,
      // Analytics only. The backend exempts the tutorial's mark from the
      // daily quota via its own SWAPLIST_TUTORIAL task, not this field.
      source: where,
      triggeredFromSection
    });

    // Only present in superlike responses: the balance after this mark.
    if (typeof response.superLikesRemaining === 'number') {
      context.commit(
        'menu/setSuperLikesRemaining',
        response.superLikesRemaining,
        {
          root: true
        }
      );
    }

    if (response.success === true) {
      context.dispatch('interests/setMarkInterestDone', true, { root: true });
      // Byteslistan 3.0: record the last successful mark so the swap list can
      // dismiss a card answered from its proposition detail page. The list
      // clears this when a detail OPENS (so only an in-detail mark is armed) and
      // again when it consumes the mark on detail-close (#2).
      context.commit('setLastMarked', {
        propositionId: targetPropositionId,
        interested
      });
      // Deliberately NO dispatch of swapList/markPropositionForNoInterest
      // here: that action re-dispatches markInterest for no-marks, and since
      // nothing ever populates noInterestMarkedIds its already-marked guard
      // can't break the cycle — every successful no-mark fired duplicate
      // requests until the backend stopped answering success:true.

      // Show +1 badge
      if (interested) {
        context.dispatch('swapList/showSwapAddedBadge', interested, {
          root: true
        });
      }

      context.dispatch('swapList/getSwapInterestCounts', null, {
        root: true
      });

      context
        .dispatch('menu/fetchNotifications', currentUserProposition, {
          root: true
        })
        .then(notifications => {
          // fetchNotifications resolves undefined when its request fails —
          // the like already succeeded, only skip the badge refresh (KL-X).
          if (!notifications) return;

          context.dispatch(
            'swapList/setNewSwapsCount',
            notifications.newSwaps,
            {
              root: true
            }
          );

          context.dispatch(
            'swapList/setUnansweredCount',
            notifications.unansweredSwaps,
            {
              root: true
            }
          );
        })
        .catch(err => window.Logger.log(err));
    } else if (isSuperLikeNoOp(isSuperLike, response)) {
      // Idempotent no-op: this ad was already superliked (local state is lost
      // on reload). The balance is untouched — just keep the star filled.
      context.commit('addSuperLiked', targetPropositionId);
    } else if (response.remaining === 0) {
      events.emitEvent(events.eventTypes.SEE, 'Paywall', {
        trigger: 'interest-mark',
        src: where
      });
    }

    await context.dispatch('showLikeInfo', {
      ...response,
      interested,
      targetPropositionId,
      superLike: isSuperLike,
      previousInterested
    });

    context.dispatch(
      'gtm/interestMarked',
      {
        propositionId: currentUserProposition.toString(),
        propositionId2: targetPropositionId.toString(),
        interest,
        where
      },
      { root: true }
    );

    // Flag failure so SuggestionCard can revert its local state (a superlike
    // no-op without error is a success, see above)
    if (
      interested === true &&
      response.success !== true &&
      !isSuperLikeNoOp(isSuperLike, response)
    ) {
      likeFailed = true;
    }
  } catch (e) {
    // Revert optimistic Vuex state on network error
    if (interested === true) {
      revertInterest(context, {
        targetPropositionId,
        previousInterested,
        superLike: isSuperLike
      });
    } else if (wasSuperLiked) {
      // The backend still holds the superlike: put the star back.
      context.dispatch('setInterestStatus', {
        targetPropositionId,
        interested: true,
        superLike: true
      });
    }
    window.Logger.log("ERROR: Couldn't mark interest");
    throw e;
  }

  // Reject after try/catch so SuggestionCard reverts its local state
  // (showLikeInfo already handled Vuex revert and modals)
  if (likeFailed) {
    throw new Error('Interest mark failed');
  }
}

function setInterestStatus(
  context,
  { targetPropositionId, interested, superLike = false }
) {
  // Superlike is a third state only here: downstream it is a plain yes.
  if (superLike && interested === true) {
    context.commit('addSuperLiked', targetPropositionId);
  } else if (interested !== true) {
    context.commit('removeSuperLiked', targetPropositionId);
  }

  // propositions
  context.dispatch(
    'propositions/setSwapInterest',
    { targetPropositionId, interested },
    { root: true }
  );

  // swap list & swap chain
  context.dispatch(
    'swapList/setSwapInterest',
    { targetPropositionId, interested },
    { root: true }
  );

  // suggestions map (fullscreen map card cache)
  context.commit(
    'suggestionsMap/UPDATE_INTEREST_STATE',
    { propositionId: targetPropositionId, interested },
    { root: true }
  );
}

// The backend's same-level no-op is the only errorless success:false that
// also echoes the balance (InterestController, "currentInterest.Value ==
// model.Interested"). Any other errorless refusal is a failure.
function isSuperLikeNoOp(isSuperLike, response) {
  return (
    isSuperLike &&
    !response.error &&
    typeof response.superLikesRemaining === 'number'
  );
}

// Restore the mark a refused request started from. Plain likes always fall
// back to unmarked (previousInterested undefined → null).
function revertInterest(
  context,
  { targetPropositionId, previousInterested, superLike }
) {
  context.dispatch('setInterestStatus', {
    targetPropositionId,
    interested: previousInterested === undefined ? null : previousInterested
  });
  if (superLike) {
    context.commit('removeSuperLiked', targetPropositionId);
  }
}

async function showLikeInfo(
  context,
  {
    remaining,
    success,
    interested,
    targetPropositionId,
    error,
    superLike,
    previousInterested
  }
) {
  const revert = () =>
    revertInterest(context, {
      targetPropositionId,
      previousInterested,
      superLike
    });

  if (error === 'SUPERLIKE_NOT_ALLOWED') {
    // Freemium/trial ad: the star is shown as an upsell, so this is the
    // fallback if the button is clicked before the balance is known.
    context.dispatch('ui/setGlobalModal', 'SuperInterestPaywall', {
      root: true
    });
    events.emitEvent(events.eventTypes.SEE, 'Paywall', {
      trigger: 'super-interest-not-allowed',
      src: 'interest-mark'
    });
    revert();
    return;
  }

  if (error === 'SUPERLIKE_NO_REMAINING') {
    // Also hit on a race between two quick clicks — sync the balance to 0.
    context.commit('menu/setSuperLikesRemaining', 0, { root: true });
    context.dispatch('superLikes/openPurchase', null, { root: true });
    events.emitEvent(events.eventTypes.SEE, 'Paywall', {
      trigger: 'super-interest-out-of-quota',
      src: 'interest-mark'
    });
    revert();
    return;
  }

  if (error === 'SUPERLIKE_UNAVAILABLE') {
    // The backend could not read the balance (not "0"): plain retry message.
    context.dispatch(
      'toast/setToast',
      { timer: 8, title: i18n.t('toast_general_error_message', routeLocale) },
      { root: true }
    );
    revert();
    return;
  }

  if (error === 'IS_PREMIUM_SUGGESTION') {
    context.dispatch('ui/setGlobalModal', 'GetUnlimitedLikes', {
      root: true
    });

    // Reset interest mark
    revert();

    return;
  }

  if (interested) {
    // If limited due to too many unanswered
    if (error === 'TOO_MANY_UNANSWERED') {
      context.dispatch('ui/setGlobalModal', 'NewInterestLimited', {
        root: true
      });

      events.emitEvent(events.eventTypes.SEE, 'Interest Limitation', {
        reason: 'Too many unanswered'
      });

      // Remove like
      revert();
    } else {
      // Show no likes left modal
      if (remaining === 0 && success === false) {
        context.dispatch('ui/setGlobalModal', 'NoLikesLeft', {
          root: true
        });

        context.dispatch('setNoInterestMarksLeft', true);

        if (
          context.rootGetters['myPropositions/primaryPropositionIsLimited'] &&
          context.rootState.swapList.swapFilterType ===
            SWAP_FILTER_TYPES.REMOVED
        ) {
          // Keep interestStatus false
          context.dispatch('setInterestStatus', {
            targetPropositionId,
            interested: false
          });
        } else {
          // Remove like
          revert();
        }

        events.emitEvent(events.eventTypes.SEE, 'Interest Limitation', {
          reason: 'No likes left'
        });
      }
    }
  }
}

async function setMarkInterestDone(context, value) {
  context.commit('setMarkInterestDone', value);
}

async function setNoInterestMarksLeft(context, value) {
  context.commit('setNoInterestMarksLeft', value);
}

export default {
  markInterest,
  setInterestStatus,
  showLikeInfo,
  setMarkInterestDone,
  setNoInterestMarksLeft,
  init
};
